import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Paperclip, RefreshCw, Search, Download, Eye, X, Check, AlertCircle,
    FileText, Image, Upload, ChevronDown, ChevronRight, Loader, Clock,
    Link2, Trash2, Zap, CheckCircle, XCircle, ExternalLink, FolderOpen,
    Grid, List, Calendar, Filter, Send
} from 'lucide-react';
import { Worker, Viewer, SpecialZoomLevel, ScrollMode } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const PDFJS_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

/**
 * 톡클라우드 문서관리 - 메인 컴포넌트
 * 톡클라우드에서 수집한 파일/사진을 탐색하고 문서인식으로 전달하는 화면
 */
const TalkCloudDocManager = ({ API_BASE_URL }) => {
    // ============ 상태 관리 ============
    const [documents, setDocuments] = useState([]);
    const [collectionStatus, setCollectionStatus] = useState(null);
    const [isCollecting, setIsCollecting] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [showFetchModal, setShowFetchModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [selectedDocs, setSelectedDocs] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [collectDate, setCollectDate] = useState(new Date().toISOString().split('T')[0]);
    const [collectType, setCollectType] = useState('both');
    const [maxCount, setMaxCount] = useState(0);
    const [memberName, setMemberName] = useState('');
    const [fetchProgress, setFetchProgress] = useState([]);
    const [taskId, setTaskId] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    const [filterType, setFilterType] = useState('all'); // 'all' | '이미지' | '문서(PDF)' | '엑셀'
    const [loading, setLoading] = useState(false);
    const pollingRef = useRef(null);

    // ============ API 호출 ============

    // 수집된 문서 목록 로드
    const loadDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/documents/talkcloud/list`);
            if (res.ok) {
                const data = await res.json();
                setDocuments(data.documents || []);
            }
        } catch (err) {
            console.error('문서 목록 로드 실패:', err);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL]);

    // 톡클라우드 문서 수집 시작
    const startFetch = async () => {
        setIsCollecting(true);
        setFetchProgress([{ step: 'init', message: '수집 준비 중...', time: new Date().toLocaleTimeString() }]);
        try {
            const kakaoEmail = document.getElementById('kakao-email')?.value;
            const kakaoPassword = document.getElementById('kakao-password')?.value;
            if (!kakaoEmail || !kakaoPassword) {
                alert('카카오 계정 정보를 입력해주세요');
                setIsCollecting(false);
                return;
            }

            const res = await fetch(`${API_BASE_URL}/api/talkcloud/fetch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    date: collectDate,
                    collect_type: collectType,
                    max_count: maxCount,
                    member: memberName,
                    kakao_id: kakaoEmail,
                    kakao_pw: kakaoPassword
                })
            });

            const data = await res.json();
            const cid = data.collectionId || data.task_id;
            if (data.success && cid) {
                setTaskId(cid);
                addProgress('started', `수집 시작 (ID: ${cid})`);
                startPolling(cid);
            } else {
                addProgress('error', `시작 실패: ${data.error || '알 수 없는 오류'}`);
                setIsCollecting(false);
            }
        } catch (err) {
            addProgress('error', `오류: ${err.message}`);
            setIsCollecting(false);
        }
    };

    // 수집 상태 폴링
    const startPolling = (tid) => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        let retryCount = 0;
        const maxRetries = 30;

        pollingRef.current = setInterval(async () => {
            retryCount++;
            try {
                const res = await fetch(`${API_BASE_URL}/api/talkcloud/status/${tid}`);
                const data = await res.json();
                setCollectionStatus(data);

                if (data.status === 'completed') {
                    addProgress('completed', `✅ 수집 완료: ${data.result?.total_files || 0}건`);
                    setIsCollecting(false);
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    loadDocuments();
                } else if (data.status === 'failed' || data.status === 'not_found' || data.status === 'unknown') {
                    addProgress('failed', `❌ 수집 실패: ${data.error_message || data.message || '알 수 없는 오류'}`);
                    setIsCollecting(false);
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                } else if (retryCount >= maxRetries) {
                    addProgress('timeout', '⏱️ 수집 시간 초과 (60초)');
                    setIsCollecting(false);
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                }
            } catch (err) {
                console.error('상태 폴링 실패:', err);
                retryCount++;
                if (retryCount >= maxRetries) {
                    setIsCollecting(false);
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                }
            }
        }, 2000);
    };

    const addProgress = (step, message) => {
        setFetchProgress(prev => [...prev, {
            step, message, time: new Date().toLocaleTimeString()
        }]);
    };

    // 폴더 스캔 → DB 등록
    const scanFiles = async () => {
        setIsScanning(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/talkcloud/scan-files`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await res.json();
            if (data.success) {
                alert(`📂 스캔 완료!\n전체: ${data.scanned}개 파일\n신규 등록: ${data.newFiles}개\n이미 등록: ${data.alreadyRegistered}개`);
                loadDocuments();
            } else {
                alert('스캔 실패: ' + (data.error || '알 수 없는 오류'));
            }
        } catch (err) {
            console.error('폴더 스캔 실패:', err);
            alert('폴더 스캔 실패: ' + err.message);
        } finally {
            setIsScanning(false);
        }
    };

    // 문서인식으로 보내기
    const sendToWorkflow = async () => {
        if (selectedDocs.size === 0) {
            alert('문서인식으로 보낼 파일을 선택해주세요');
            return;
        }
        const selected = documents.filter(d => selectedDocs.has(d.doc_id));
        // 파일을 문서인식 워크플로우에 전달 (localStorage로 전달 후 페이지 이동)
        const filesToSend = selected.map(d => ({
            doc_id: d.doc_id,
            filename: d.filename,
            file_path: d.file_path,
            doc_type: d.doc_type,
            source: 'talkcloud',
        }));
        localStorage.setItem('talkcloud_import_files', JSON.stringify(filesToSend));
        alert(`📤 ${selected.length}개 파일을 문서인식으로 전달합니다.\n문서인식 메뉴로 이동하세요.`);
    };

    // 초기 로드
    useEffect(() => {
        loadDocuments();
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [loadDocuments]);

    // ============ 필터/검색 ============
    const filteredDocuments = documents.filter(doc => {
        const matchSearch = !searchTerm ||
            doc.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doc.doc_type?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchType = filterType === 'all' || doc.doc_type === filterType;
        return matchSearch && matchType;
    });

    // 날짜별 그룹핑
    const groupedByDate = filteredDocuments.reduce((acc, doc) => {
        const dateKey = doc.source_date
            ? new Date(doc.source_date).toISOString().split('T')[0]
            : doc.collected_at
                ? new Date(doc.collected_at).toISOString().split('T')[0]
                : '날짜 미상';
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(doc);
        return acc;
    }, {});

    const sortedDates = Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a));

    // 전체 선택/해제
    const toggleSelectAll = () => {
        if (selectedDocs.size === filteredDocuments.length) {
            setSelectedDocs(new Set());
        } else {
            setSelectedDocs(new Set(filteredDocuments.map(d => d.doc_id)));
        }
    };

    const toggleDocSelect = (docId) => {
        setSelectedDocs(prev => {
            const next = new Set(prev);
            if (next.has(docId)) next.delete(docId);
            else next.add(docId);
            return next;
        });
    };

    // 파일 아이콘 결정
    const getFileIcon = (doc) => {
        const ext = doc.filename?.split('.').pop()?.toLowerCase() || '';
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return '🖼️';
        if (['pdf'].includes(ext)) return '📄';
        if (['xlsx', 'xls', 'csv'].includes(ext)) return '📊';
        if (['doc', 'docx'].includes(ext)) return '📝';
        if (['mp4', 'avi', 'mov'].includes(ext)) return '🎬';
        return '📎';
    };

    // 파일 미리보기 URL
    const getPreviewUrl = (doc) => {
        return `${API_BASE_URL}/api/talkcloud/file/${doc.doc_id}`;
    };

    // 이미지 파일 여부
    const isImageFile = (doc) => {
        const ext = doc.filename?.split('.').pop()?.toLowerCase() || '';
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
    };

    // PDF 파일 여부
    const isPdfFile = (doc) => {
        const ext = doc.filename?.split('.').pop()?.toLowerCase() || '';
        return ['pdf'].includes(ext);
    };

    // 파일 크기 포맷
    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return `${bytes}B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    };

    // 유형별 카운트
    const typeCounts = documents.reduce((acc, d) => {
        const t = d.doc_type || '기타';
        acc[t] = (acc[t] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* ======== 헤더 ======== */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
                <div className="px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Paperclip className="w-5 h-5 text-yellow-600" />
                        <h1 className="text-lg font-bold text-gray-900">톡클라우드 문서관리</h1>
                        <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                            {documents.length}건
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* 수집 상태 표시 */}
                        {collectionStatus?.status === 'completed' && (
                            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 rounded-full px-3 py-1.5">
                                <CheckCircle className="w-3.5 h-3.5" />
                                {collectionStatus.result?.total_files || 0}건 수집됨
                            </span>
                        )}
                        {/* 수집 날짜 */}
                        <input
                            type="date"
                            value={collectDate}
                            onChange={e => setCollectDate(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                        {/* 문서 가져오기 버튼 */}
                        <button
                            onClick={() => setShowFetchModal(true)}
                            disabled={isCollecting}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm ${isCollecting
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900'
                                }`}
                        >
                            {isCollecting ? (
                                <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4" />
                            )}
                            톡클라우드 문서 가져오기
                        </button>
                        {/* 폴더 스캔 버튼 */}
                        <button
                            onClick={scanFiles}
                            disabled={isScanning}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm ${isScanning
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                                }`}
                        >
                            {isScanning ? (
                                <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                                <FolderOpen className="w-4 h-4" />
                            )}
                            {isScanning ? '스캔 중...' : '📂 폴더 스캔'}
                        </button>
                    </div>
                </div>
            </div>

            {/* ======== 메인 콘텐츠 (좌: 파일목록, 우: 미리보기) ======== */}
            <div className="flex-1 flex overflow-hidden">
                {/* ---- 좌측: 파일 목록 ---- */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* 툴바: 검색 + 필터 + 뷰모드 + 액션 */}
                    <div className="flex-shrink-0 px-4 py-2 bg-white border-b border-gray-100 flex items-center gap-3">
                        {/* 검색 */}
                        <div className="relative flex-1 max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder="파일명 검색..."
                                className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                        </div>
                        {/* 유형 필터 */}
                        <div className="flex items-center gap-1">
                            {[
                                { key: 'all', label: '전체', count: documents.length },
                                { key: '이미지', label: '🖼️ 이미지', count: typeCounts['이미지'] || 0 },
                                { key: '문서(PDF)', label: '📄 PDF', count: typeCounts['문서(PDF)'] || 0 },
                                { key: '엑셀', label: '📊 엑셀', count: typeCounts['엑셀'] || 0 },
                            ].map(f => (
                                <button
                                    key={f.key}
                                    onClick={() => setFilterType(f.key)}
                                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${filterType === f.key
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'text-gray-500 hover:bg-gray-100'
                                        }`}
                                >
                                    {f.label} ({f.count})
                                </button>
                            ))}
                        </div>
                        {/* 뷰 모드 */}
                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-1.5 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <Grid className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-1.5 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <List className="w-4 h-4" />
                            </button>
                        </div>
                        {/* 전체 선택 */}
                        <button
                            onClick={toggleSelectAll}
                            className="text-xs text-gray-500 hover:text-blue-600 px-2 py-1 rounded"
                        >
                            {selectedDocs.size === filteredDocuments.length && filteredDocuments.length > 0
                                ? '전체 해제' : '전체 선택'}
                        </button>
                        {/* 새로고침 */}
                        <button
                            onClick={loadDocuments}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="새로고침"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>
                    </div>

                    {/* 선택된 파일 액션 바 */}
                    {selectedDocs.size > 0 && (
                        <div className="flex-shrink-0 px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                            <span className="text-sm font-medium text-blue-800">
                                ✅ {selectedDocs.size}개 파일 선택됨
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={sendToWorkflow}
                                    className="flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-sm font-medium hover:from-green-600 hover:to-green-700 transition shadow-sm"
                                >
                                    <Send className="w-3.5 h-3.5" />
                                    문서인식으로 보내기
                                </button>
                                <button
                                    onClick={() => setSelectedDocs(new Set())}
                                    className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                                >
                                    선택 해제
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 파일 목록 */}
                    <div className="flex-1 overflow-auto p-4">
                        {loading ? (
                            <div className="flex items-center justify-center h-full">
                                <Loader className="w-8 h-8 text-blue-500 animate-spin" />
                            </div>
                        ) : filteredDocuments.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                <FolderOpen className="w-16 h-16 mb-4 text-gray-300" />
                                <p className="text-lg font-medium text-gray-500">수집된 문서가 없습니다</p>
                                <p className="text-sm mt-2">
                                    "톡클라우드 문서 가져오기"를 실행하거나<br />
                                    "📂 폴더 스캔"으로 기존 파일을 등록하세요
                                </p>
                            </div>
                        ) : viewMode === 'grid' ? (
                            /* ---- 그리드 뷰 (날짜별 그룹) ---- */
                            <div className="space-y-6">
                                {sortedDates.map(dateKey => (
                                    <div key={dateKey}>
                                        <div className="flex items-center gap-2 mb-3">
                                            <Calendar className="w-4 h-4 text-gray-400" />
                                            <h3 className="text-sm font-bold text-gray-700">{dateKey}</h3>
                                            <span className="text-xs text-gray-400">
                                                ({groupedByDate[dateKey].length}건)
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3">
                                            {groupedByDate[dateKey].map(doc => (
                                                <div
                                                    key={doc.doc_id}
                                                    className={`relative group cursor-pointer rounded-xl border-2 transition overflow-hidden ${selectedDocs.has(doc.doc_id)
                                                        ? 'border-blue-500 bg-blue-50 shadow-md'
                                                        : selectedDocument?.doc_id === doc.doc_id
                                                            ? 'border-yellow-400 bg-yellow-50 shadow-md'
                                                            : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                                                        }`}
                                                    onClick={() => setSelectedDocument(doc)}
                                                >
                                                    {/* 체크박스 */}
                                                    <div className="absolute top-1.5 left-1.5 z-10">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDocs.has(doc.doc_id)}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                toggleDocSelect(doc.doc_id);
                                                            }}
                                                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                            onClick={e => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    {/* 썸네일 */}
                                                    <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                                                        {isImageFile(doc) ? (
                                                            <img
                                                                src={getPreviewUrl(doc)}
                                                                alt={doc.filename}
                                                                className="w-full h-full object-cover"
                                                                loading="lazy"
                                                                onError={e => { e.target.style.display = 'none'; }}
                                                            />
                                                        ) : (
                                                            <span className="text-3xl">{getFileIcon(doc)}</span>
                                                        )}
                                                    </div>
                                                    {/* 파일명 */}
                                                    <div className="px-2 py-1.5">
                                                        <p className="text-[10px] text-gray-700 truncate font-medium" title={doc.filename}>
                                                            {doc.filename}
                                                        </p>
                                                        <div className="flex items-center justify-between mt-0.5">
                                                            <span className="text-[9px] text-gray-400">
                                                                {formatFileSize(doc.file_size)}
                                                            </span>
                                                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${doc.doc_type === '이미지' ? 'bg-purple-100 text-purple-700' :
                                                                doc.doc_type === '문서(PDF)' ? 'bg-red-100 text-red-700' :
                                                                    doc.doc_type === '엑셀' ? 'bg-green-100 text-green-700' :
                                                                        'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                {doc.doc_type || '기타'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* ---- 리스트 뷰 ---- */
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr className="border-b border-gray-200">
                                        <th className="w-8 px-2 py-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedDocs.size === filteredDocuments.length && filteredDocuments.length > 0}
                                                onChange={toggleSelectAll}
                                                className="w-4 h-4 rounded border-gray-300 text-blue-600"
                                            />
                                        </th>
                                        <th className="px-3 py-2 text-left font-semibold text-gray-600">파일명</th>
                                        <th className="px-3 py-2 text-left font-semibold text-gray-600 w-24">유형</th>
                                        <th className="px-3 py-2 text-right font-semibold text-gray-600 w-20">크기</th>
                                        <th className="px-3 py-2 text-left font-semibold text-gray-600 w-28">수집일</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDocuments.map(doc => (
                                        <tr
                                            key={doc.doc_id}
                                            className={`border-b border-gray-100 cursor-pointer transition ${selectedDocument?.doc_id === doc.doc_id
                                                ? 'bg-yellow-50'
                                                : 'hover:bg-gray-50'
                                                }`}
                                            onClick={() => setSelectedDocument(doc)}
                                        >
                                            <td className="px-2 py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDocs.has(doc.doc_id)}
                                                    onChange={() => toggleDocSelect(doc.doc_id)}
                                                    onClick={e => e.stopPropagation()}
                                                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                                                />
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-2">
                                                    <span>{getFileIcon(doc)}</span>
                                                    <span className="truncate max-w-xs" title={doc.filename}>
                                                        {doc.filename}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${doc.doc_type === '이미지' ? 'bg-purple-100 text-purple-700' :
                                                    doc.doc_type === '문서(PDF)' ? 'bg-red-100 text-red-700' :
                                                        doc.doc_type === '엑셀' ? 'bg-green-100 text-green-700' :
                                                            'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {doc.doc_type || '기타'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-500">
                                                {formatFileSize(doc.file_size)}
                                            </td>
                                            <td className="px-3 py-2 text-gray-500">
                                                {doc.source_date
                                                    ? new Date(doc.source_date).toLocaleDateString('ko-KR')
                                                    : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* ---- 우측: 미리보기 패널 ---- */}
                <div className="w-[50%] flex-shrink-0 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
                    {selectedDocument ? (
                        <>
                            {/* 미리보기 헤더 */}
                            <div className="flex-shrink-0 px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 flex items-center justify-between">
                                <h3 className="text-sm font-bold text-blue-800 truncate flex-1" title={selectedDocument.filename}>
                                    {selectedDocument.filename}
                                </h3>
                                <button
                                    onClick={() => setSelectedDocument(null)}
                                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* 미리보기 본문 */}
                            <div className="flex-1 overflow-auto">
                                {/* 이미지/PDF 미리보기 */}
                                <div className="bg-gray-100 flex items-center justify-center flex-1 min-h-[500px] p-4">
                                    {isImageFile(selectedDocument) ? (
                                        <img
                                            src={getPreviewUrl(selectedDocument)}
                                            alt={selectedDocument.filename}
                                            className="max-w-full max-h-[calc(100vh-280px)] object-contain rounded-lg shadow"
                                        />
                                    ) : isPdfFile(selectedDocument) ? (
                                        <div className="w-full h-full min-h-[500px]">
                                            <Worker workerUrl={PDFJS_WORKER_URL}>
                                                <Viewer
                                                    fileUrl={getPreviewUrl(selectedDocument)}
                                                    defaultScale={SpecialZoomLevel.PageWidth}
                                                    scrollMode={ScrollMode.Vertical}
                                                    renderError={(error) => (
                                                        <div className="text-center p-5 bg-red-50 text-red-500">
                                                            <p className="font-bold">PDF 로드 오류</p>
                                                            <p className="text-sm mt-1">{error.message}</p>
                                                        </div>
                                                    )}
                                                />
                                            </Worker>
                                        </div>
                                    ) : (
                                        <div className="text-center text-gray-400">
                                            <span className="text-5xl">{getFileIcon(selectedDocument)}</span>
                                            <p className="text-sm mt-3 font-medium">{selectedDocument.filename}</p>
                                        </div>
                                    )}
                                </div>

                                {/* 파일 정보 */}
                                <div className="p-4 space-y-3">
                                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">파일 정보</h4>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { label: '유형', value: selectedDocument.doc_type || '미분류' },
                                            { label: '크기', value: formatFileSize(selectedDocument.file_size) },
                                            { label: '수집일', value: selectedDocument.source_date ? new Date(selectedDocument.source_date).toLocaleDateString('ko-KR') : '-' },
                                            { label: '발신자', value: selectedDocument.source_sender || '-' },
                                        ].map(item => (
                                            <div key={item.label} className="bg-gray-50 rounded-lg p-2.5">
                                                <span className="text-[10px] text-gray-400 uppercase">{item.label}</span>
                                                <p className="text-sm font-medium text-gray-900 truncate">{item.value}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* 액션 버튼 */}
                                    <div className="space-y-2 pt-2">
                                        <button
                                            onClick={() => {
                                                setSelectedDocs(new Set([selectedDocument.doc_id]));
                                                sendToWorkflow();
                                            }}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl text-sm font-bold hover:from-green-600 hover:to-green-700 transition shadow-sm"
                                        >
                                            <Send className="w-4 h-4" />
                                            문서인식으로 보내기
                                        </button>
                                        <a
                                            href={getPreviewUrl(selectedDocument)}
                                            download={selectedDocument.filename}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition"
                                        >
                                            <Download className="w-4 h-4" />
                                            다운로드
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-400">
                            <div className="text-center">
                                <Eye className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-sm font-medium">파일을 선택하세요</p>
                                <p className="text-xs mt-1">좌측에서 파일을 클릭하면 미리보기를 볼 수 있습니다</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* =============== 톡클라우드 문서 가져오기 팝업 =============== */}
            {showFetchModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-h-[80vh] overflow-hidden flex flex-col">
                        {/* 팝업 헤더 */}
                        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-yellow-50 to-orange-50">
                            <div className="flex items-center justify-between">
                                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                                    <Download className="w-5 h-5 text-yellow-600" />
                                    톡클라우드 문서 가져오기
                                </h2>
                                <button
                                    onClick={() => { setShowFetchModal(false); setFetchProgress([]); }}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* 팝업 본문 */}
                        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
                            {/* 카카오 계정 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">카카오 계정</label>
                                <input
                                    type="email"
                                    placeholder="kakao@kakao.com"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                                    id="kakao-email"
                                />
                                <input
                                    type="password"
                                    placeholder="비밀번호"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                                    id="kakao-password"
                                />
                            </div>

                            {/* 수집 날짜 */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">수집 날짜</label>
                                <input
                                    type="date"
                                    value={collectDate}
                                    onChange={e => setCollectDate(e.target.value)}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                                />
                            </div>

                            {/* 대화상대 선택 */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">대화상대 (선택)</label>
                                <input
                                    type="text"
                                    value={memberName}
                                    onChange={e => setMemberName(e.target.value)}
                                    placeholder="비워두면 전체 (예: 로긴, CS팀, 홍길동)"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                                />
                                <p className="text-xs text-gray-400 mt-1">대화상대 이름의 일부만 입력해도 검색됩니다</p>
                            </div>

                            {/* 수집 대상 */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">수집 대상</label>
                                <div className="space-y-2">
                                    {[
                                        { value: 'both', label: '📦 전체 (파일 + 사진)' },
                                        { value: 'files_only', label: '📄 파일만' },
                                        { value: 'photos_only', label: '🖼️ 사진만' },
                                    ].map(opt => (
                                        <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${collectType === opt.value
                                            ? 'border-yellow-400 bg-yellow-50'
                                            : 'border-gray-200 hover:bg-gray-50'
                                            }`}>
                                            <input
                                                type="radio"
                                                name="collectType"
                                                value={opt.value}
                                                checked={collectType === opt.value}
                                                onChange={e => setCollectType(e.target.value)}
                                                className="w-4 h-4 text-yellow-500 focus:ring-yellow-500"
                                            />
                                            <span className="text-sm">{opt.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* 수집 개수 */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">수집 개수</label>
                                <select
                                    value={maxCount}
                                    onChange={e => setMaxCount(Number(e.target.value))}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                                >
                                    <option value={0}>전체</option>
                                    <option value={5}>최근 5개</option>
                                    <option value={10}>최근 10개</option>
                                    <option value={20}>최근 20개</option>
                                    <option value={50}>최근 50개</option>
                                </select>
                            </div>

                            {/* 수집 진행 상태 */}
                            {fetchProgress.length > 0 && (
                                <div className="bg-gray-900 rounded-xl p-4 space-y-1 max-h-40 overflow-auto">
                                    {fetchProgress.map((p, i) => (
                                        <div key={i} className="flex items-start gap-2 text-xs">
                                            <span className="text-gray-500 min-w-[55px]">{p.time}</span>
                                            <span className={`flex-1 ${p.step === 'completed' ? 'text-green-400' :
                                                p.step === 'failed' || p.step === 'error' ? 'text-red-400' :
                                                    'text-gray-300'
                                                }`}>
                                                {p.message}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 팝업 푸터 */}
                        <div className="flex-shrink-0 px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-between">
                            <button
                                onClick={() => { setShowFetchModal(false); setFetchProgress([]); }}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg transition"
                            >
                                닫기
                            </button>
                            <button
                                onClick={startFetch}
                                disabled={isCollecting}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition shadow-sm ${isCollecting
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900'
                                    }`}
                            >
                                {isCollecting ? (
                                    <><Loader className="w-4 h-4 animate-spin" /> 수집 중...</>
                                ) : (
                                    <><Download className="w-4 h-4" /> 수집 시작</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TalkCloudDocManager;
